// services/axiosInstance.js
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { v4 as uuidv4 } from 'uuid';

// URLs de production - JAMAIS utiliser back-pharos.mbscom.net
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://varras.mbscom.net/api';
let FRONTEND_URL = process.env.REACT_APP_API_URL_FRONT || 'https://varras.mbscom.net/api/frontoffice';

// Validation et correction automatique pour éviter les mauvaises URLs
if (FRONTEND_URL.includes('back-pharos.mbscom.net')) {
  console.error('⚠️ ATTENTION: URL incorrecte détectée! Correction automatique appliquée.');
  FRONTEND_URL = FRONTEND_URL.replace('back-pharos.mbscom.net', 'varras.mbscom.net');
}

const axiosInstance = axios.create({
  baseURL: FRONTEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interception pour ajouter le token
axiosInstance.interceptors.request.use(config => {
  // Ne pas ajouter d'autorisation si c'est une requête de refresh ou si skipAuthRefresh est défini
  if (config.skipAuthRefresh || config.url.includes('/token/refresh')) {
    config.headers['X-Correlation-ID'] = uuidv4();
    return config;
  }
  
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = 'Bearer ' + token;
  }
  config.headers['X-Correlation-ID'] = uuidv4();
  return config;
});

// Variable pour éviter les refreshs multiples simultanés
let refreshPromise = null;

// Fonction de refresh automatique du token
const refreshAuthLogic = failedRequest => {
  // Ignorer les requêtes OPTIONS (CORS preflight)
  if (failedRequest?.response?.config?.method?.toUpperCase() === 'OPTIONS') {
    return Promise.reject(failedRequest);
  }

  // Si un refresh est déjà en cours, attendre qu'il se termine
  if (refreshPromise) {
    return refreshPromise.then(() => {
      const token = localStorage.getItem('token');
      if (token) {
        failedRequest.response.config.headers['Authorization'] = 'Bearer ' + token;
        return Promise.resolve();
      }
      return Promise.reject(new Error("Token non disponible après refresh"));
    });
  }

  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    // Ne rediriger que si on n'est pas déjà sur la page de login et si c'est pas une requête skipAuthRefresh
    if (window.location.pathname !== "/login" && !failedRequest.response.config.skipAuthRefresh) {
      localStorage.removeItem('token');
      window.location.href = '/login?error=session_expired';
    }
    return Promise.reject(new Error("Aucun refresh token"));
  }

  // Créer une instance sans interceptors pour éviter les boucles
  const refreshAxios = axios.create({
    baseURL: FRONTEND_URL,
    headers: { 'Content-Type': 'application/json' }
  });

  // Créer la promesse de refresh
  refreshPromise = refreshAxios.post('/token/refresh', {
    refresh_token: refreshToken
  }).then(response => {
    const newToken = response.data.token;
    localStorage.setItem('token', newToken);
    failedRequest.response.config.headers['Authorization'] = 'Bearer ' + newToken;
    refreshPromise = null;
    return Promise.resolve();
  }).catch(err => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    refreshPromise = null;
    
    // Ne rediriger que si on n'est pas déjà sur la page de login
    if (window.location.pathname !== "/login") {
      window.location.href = '/login?error=session_expired';
    }
    return Promise.reject(err);
  });

  return refreshPromise;
};

// Configuration plus robuste de l'interceptor
createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic, {
  statusCodes: [401],
  retryInstance: axiosInstance,
  pauseInstanceWhileRefreshing: true
});

export default axiosInstance;
